/**
 * Tailwind v4 Animation Optimizer
 * 
 * Integrates with Tailwind CSS v4 for optimal animation performance using:
 * - tailwindcss-animate plugin patterns
 * - GPU acceleration with will-change
 * - Intelligent caching for repeated animations
 * - Reduced motion accessibility support
 * - Production-optimized CSS minification strategies
 */

import { 
  cacheAnimationState, 
  applyCachedAnimation, 
  promoteToRenderLayer, 
  setupSmartAnimationTrigger,
  getAnimationCacheStats,
  batchPromoteElements
} from './animationCacheService';
import { logDebug, logInfo } from './logger';

// Tailwind v4 animation classes with performance optimizations
export const TAILWIND_ANIMATIONS = {
  // Built-in Tailwind animations
  spin: 'animate-spin',
  ping: 'animate-ping',
  pulse: 'animate-pulse',
  bounce: 'animate-bounce',
  
  // Custom high-performance animations (following tailwindcss-animate patterns)
  fadeIn: 'animate-fade-in',
  fadeOut: 'animate-fade-out',
  slideInUp: 'animate-slide-in-up',
  slideInDown: 'animate-slide-in-down',
  slideInLeft: 'animate-slide-in-left',
  slideInRight: 'animate-slide-in-right',
  scaleIn: 'animate-scale-in',
  scaleOut: 'animate-scale-out',
  rotateIn: 'animate-rotate-in',
  zoomIn: 'animate-zoom-in',
  
  // Performance-optimized variants
  fastFadeIn: 'animate-fade-in duration-300',
  slowFadeIn: 'animate-fade-in duration-1000',
  smoothScale: 'animate-scale-in duration-200 ease-out',
  gentleBounce: 'animate-bounce duration-1000',
  
  // Accessibility-aware animations
  reduceMotionFadeIn: 'animate-fade-in motion-reduce:animate-none',
  reduceMotionSlideIn: 'animate-slide-in-up motion-reduce:animate-none',
} as const;

// Performance optimization classes
export const PERFORMANCE_CLASSES = {
  gpuAcceleration: 'transform-gpu',
  willChangeTransform: 'will-change-transform',
  willChangeAuto: 'will-change-auto',
  backfaceHidden: 'backface-visibility-hidden',
  preserve3d: 'transform-style-preserve-3d',
} as const;

// Responsive animation utilities
export const RESPONSIVE_ANIMATIONS = {
  mobile: {
    reduced: 'sm:animate-none',
    fast: 'sm:duration-200',
    slow: 'sm:duration-500',
  },
  tablet: {
    normal: 'md:animate-fade-in',
    enhanced: 'md:animate-slide-in-up',
  },
  desktop: {
    full: 'lg:animate-zoom-in',
    complex: 'lg:animate-rotate-in',
  },
} as const;

/**
 * Enhanced Tailwind Animation Manager
 */
export class TailwindAnimationOptimizer {
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<HTMLElement>();
  private performanceMode: 'high' | 'balanced' | 'minimal' = 'balanced';

  constructor(options?: {
    performanceMode?: 'high' | 'balanced' | 'minimal';
    enableReducedMotion?: boolean;
  }) {
    this.performanceMode = options?.performanceMode || 'balanced';
    this.initializeOptimizer();
  }

  /**
   * Initialize the optimizer with performance settings
   */
  private initializeOptimizer(): void {
    this.setupReducedMotionDetection();
    this.setupPerformanceObserver();
    logInfo('Tailwind Animation Optimizer initialized');
  }

  /**
   * Setup reduced motion detection for accessibility
   */
  private setupReducedMotionDetection(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      
      if (prefersReducedMotion.matches) {
        document.documentElement.classList.add('motion-reduce');
        this.performanceMode = 'minimal';
        logInfo('Reduced motion detected - using minimal animations');
      }

      // Listen for changes
      prefersReducedMotion.addEventListener('change', (e) => {
        if (e.matches) {
          document.documentElement.classList.add('motion-reduce');
          this.performanceMode = 'minimal';
        } else {
          document.documentElement.classList.remove('motion-reduce');
          this.performanceMode = 'balanced';
        }
      });
    }
  }

  /**
   * Setup performance observer for animation monitoring
   */
  private setupPerformanceObserver(): void {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'measure' && entry.name.includes('animation')) {
              if (entry.duration > 16) { // > 1 frame at 60fps
                logDebug(`Slow animation detected: ${entry.name} (${entry.duration}ms)`);
              }
            }
          }
        });
        
        observer.observe({ entryTypes: ['measure'] });
      } catch (error) {
        logDebug('Performance Observer not supported');
      }
    }
  }

  /**
   * Apply optimized Tailwind animation to element
   */
  public animateElement(
    element: HTMLElement,
    animationType: keyof typeof TAILWIND_ANIMATIONS,
    options?: {
      delay?: number;
      duration?: number;
      cache?: boolean;
      promote?: boolean;
      onComplete?: () => void;
    }
  ): void {
    const { delay = 0, duration, cache = true, promote = true, onComplete } = options || {};

    // Promote to render layer for performance
    if (promote) {
      promoteToRenderLayer(element, true);
    }

    // Get base animation class
    let animationClasses = TAILWIND_ANIMATIONS[animationType];

    // Add performance optimizations based on mode
    if (this.performanceMode === 'high') {
      animationClasses += ` ${PERFORMANCE_CLASSES.gpuAcceleration} ${PERFORMANCE_CLASSES.willChangeTransform}`;
    }

    // Add custom duration if specified
    if (duration) {
      animationClasses += ` duration-${duration}`;
    }

    // Apply animation with delay
    const applyAnimation = () => {
      // Remove existing animation classes
      element.className = element.className.replace(/animate-\S+/g, '');
      
      // Add new animation classes
      element.className += ` ${animationClasses}`;
      
      // Cache the animation state if requested
      if (cache) {
        const elementId = element.id || element.getAttribute('data-animation-id') || 
                        `${animationType}-${Date.now()}`;
        cacheAnimationState(elementId, element);
      }

      // Track animated element
      this.animatedElements.add(element);

      // Handle completion
      if (onComplete) {
        const handleAnimationEnd = () => {
          onComplete();
          element.removeEventListener('animationend', handleAnimationEnd);
        };
        element.addEventListener('animationend', handleAnimationEnd);
      }

      logDebug(`Applied Tailwind animation: ${animationType} to element`);
    };

    if (delay > 0) {
      setTimeout(applyAnimation, delay);
    } else {
      requestAnimationFrame(applyAnimation);
    }
  }

  /**
   * Setup scroll-triggered animations with intersection observer
   */
  public setupScrollAnimations(
    elements: HTMLElement[],
    animationType: keyof typeof TAILWIND_ANIMATIONS,
    options?: {
      threshold?: number;
      rootMargin?: string;
      once?: boolean;
      stagger?: number;
    }
  ): void {
    const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', once = true, stagger = 100 } = options || {};

    // Use cached intersection observer
    this.intersectionObserver = setupSmartAnimationTrigger(
      elements,
      animationType,
      { threshold, rootMargin, once }
    );

    // Apply staggered animations
    if (stagger > 0) {
      elements.forEach((element, index) => {
        element.setAttribute('data-animation-delay', (index * stagger).toString());
      });
    }

    logInfo(`Setup scroll animations for ${elements.length} elements`);
  }

  /**
   * Batch optimize elements for animations
   */
  public batchOptimizeElements(elements: HTMLElement[]): void {
    // Batch promote elements to render layers
    batchPromoteElements(elements, true);

    // Add base performance classes
    elements.forEach(element => {
      if (this.performanceMode === 'high') {
        element.classList.add('transform-gpu', 'will-change-transform');
      }
    });

    logInfo(`Batch optimized ${elements.length} elements for animations`);
  }

  /**
   * Create responsive animation classes
   */
  public getResponsiveAnimationClasses(
    baseAnimation: keyof typeof TAILWIND_ANIMATIONS,
    breakpoints?: {
      mobile?: string;
      tablet?: string;
      desktop?: string;
    }
  ): string {
    let classes = TAILWIND_ANIMATIONS[baseAnimation];

    if (breakpoints) {
      if (breakpoints.mobile) {
        classes += ` sm:${breakpoints.mobile}`;
      }
      if (breakpoints.tablet) {
        classes += ` md:${breakpoints.tablet}`;
      }
      if (breakpoints.desktop) {
        classes += ` lg:${breakpoints.desktop}`;
      }
    }

    // Always add reduced motion support
    classes += ' motion-reduce:animate-none';

    return classes;
  }

  /**
   * Performance monitoring and cleanup
   */
  public getPerformanceStats() {
    const animationStats = getAnimationCacheStats();
    
    return {
      ...animationStats,
      animatedElements: this.animatedElements.size,
      performanceMode: this.performanceMode,
      hasIntersectionObserver: !!this.intersectionObserver,
    };
  }

  /**
   * Cleanup and optimize for memory
   */
  public cleanup(): void {
    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Reset will-change on animated elements
    this.animatedElements.forEach(element => {
      element.style.willChange = 'auto';
    });

    this.animatedElements.clear();
    logInfo('Animation optimizer cleaned up');
  }
}

/**
 * Global instance for easy access
 */
export const tailwindAnimationOptimizer = new TailwindAnimationOptimizer({
  performanceMode: 'balanced',
  enableReducedMotion: true,
});

/**
 * Utility functions for common animation patterns
 */
export const animationUtils = {
  /**
   * Animate elements on page load
   */
  animateOnLoad: (elements: HTMLElement[], stagger = 100) => {
    elements.forEach((element, index) => {
      tailwindAnimationOptimizer.animateElement(element, 'fadeIn', {
        delay: index * stagger,
        cache: true,
        promote: true,
      });
    });
  },

  /**
   * Setup hover animations
   */
  setupHoverAnimations: (elements: HTMLElement[]) => {
    elements.forEach(element => {
      element.addEventListener('mouseenter', () => {
        element.classList.add('animate-scale-in', 'duration-200');
      });
      
      element.addEventListener('mouseleave', () => {
        element.classList.remove('animate-scale-in');
      });
    });
  },

  /**
   * Create loading animation
   */
  createLoadingAnimation: (element: HTMLElement) => {
    tailwindAnimationOptimizer.animateElement(element, 'pulse', {
      cache: false,
      promote: true,
    });
  },

  /**
   * Optimize for mobile performance
   */
  optimizeForMobile: () => {
    if (window.innerWidth < 768) {
      // Use faster, simpler animations on mobile
      document.documentElement.style.setProperty('--animation-duration', '0.2s');
      document.documentElement.classList.add('mobile-optimized');
    }
  },
};

// Auto-initialize mobile optimizations
if (typeof window !== 'undefined') {
  animationUtils.optimizeForMobile();
  
  // Re-optimize on resize
  window.addEventListener('resize', animationUtils.optimizeForMobile);
} 